// ============================================================================
// PARTY (Client/Customer) SERVICE — entity-scoped via X-Entity-ID.
// ============================================================================

import { get, post, put, del } from '../client/axiosClient';
import { partyEndpoints } from '../endpoints/commercial.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, BackendPagedResult, PaginatedResponse, EmptyResponse, ID,
  PartyDto, CreatePartyRequest, UpdatePartyRequest, PartyQueryParams,
} from '../types';

class PartyService {
  async getAll(params?: PartyQueryParams): Promise<PaginatedResponse<PartyDto>> {
    const endpoint = params ? buildEndpointWithQuery(partyEndpoints.list(), params) : partyEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<PartyDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success, message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page, pageSize: paged.pageSize, totalPages: paged.totalPages,
        totalItems: paged.totalCount, hasNextPage: paged.hasNext, hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  async getById(id: ID): Promise<PartyDto> {
    const response = await get<ApiResponse<PartyDto>>(partyEndpoints.getById(id));
    return response.data.data;
  }

  async create(data: CreatePartyRequest): Promise<PartyDto> {
    const response = await post<ApiResponse<PartyDto>>(partyEndpoints.create(), data);
    return response.data.data;
  }

  async update(id: ID, data: UpdatePartyRequest): Promise<PartyDto> {
    const response = await put<ApiResponse<PartyDto>>(partyEndpoints.update(id), data);
    return response.data.data;
  }

  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const url = buildEndpointWithQuery(partyEndpoints.delete(id), { rowVersion });
    const response = await del<EmptyResponse>(url);
    return response.data;
  }

  /** The code the next customer would receive when none is typed. Consumes no serial. */
  async getNextCode(): Promise<string> {
    const response = await get<ApiResponse<string>>(partyEndpoints.nextCode());
    return response.data.data;
  }

  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = partyEndpoints.validateCode(code);
    const url = excludeId ? buildEndpointWithQuery(endpoint, { excludeId }) : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const partyService = new PartyService();
export default partyService;
